/**
 * "Nytt från bokningsmail" – klistra in Comdevas bokningsmail en gång,
 * granska kund, kontakt, lead och möte, tryck Skapa. Därefter öppnas
 * kallelsen förifylld i Outlook på webben (eller som ICS) för granskning –
 * Johan skickar själv. Ingen automatisk sändning.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField, Button,
  MenuItem, Alert, Chip, CircularProgress, Paper, Divider, Link, RadioGroup, Radio,
  FormControlLabel, Tooltip,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  tolkaBokningsmail, matchaKund, matchaKontakt, oppnaLeads, giltigtOrgNr, orgNrSiffror,
  formateraOrgNr, outlookDeeplink, moteForIcs, allabolagSokUrl,
  normaliseraForetagsnamn, LEAD_KALLA_COMDEVA,
} from './bokningsmail';
import { isFlatboxProduct } from './flatboxLead';
import {
  laddaKontakter, hamtaBolag, sokForetag, skapaFranBokningsmail, markeraKallelseOppnad,
} from './bokningsmailData';
import {
  buildIcs, downloadIcs, icsFilename, flatboxSubject, flatboxStandardText, loadMeetingSignature,
} from '../../utils/outlookMeeting';
import KopieraKnapp from './KopieraKnapp';

const TOM_KUND = {
  val: 'ny', namn: '', orgNr: '', ort: '', telefon: '', bransch: '', antalAnstallda: '',
  omsattning: '', webbplats: '', comdevaKundnummer: '',
};
const TOM_KONTAKT = { val: 'ny', fornamn: '', efternamn: '', titel: '', mobil: '', epost: '' };
const TOM_MOTE = { start: '', slut: '', plats: '', amne: '', kallelsetext: '' };

function Block({ nr, titel, hoger, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#FF6B00', color: 'white',
          fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {nr}
        </Box>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>{titel}</Typography>
        {hoger}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{children}</Box>
    </Paper>
  );
}

const rad = { display: 'flex', gap: 1.5, flexWrap: 'wrap' };
const f = (flex = '1 1 180px') => ({ flex });

export default function BokningsmailDialog({
  open, onClose, companies, leads, products, departments, currentUser, userProfile, navigate, onSkapad,
}) {
  const [raw, setRaw] = useState('');
  const [tolkad, setTolkad] = useState(null);
  const [kund, setKund] = useState(TOM_KUND);
  const [match, setMatch] = useState({ via: null, kandidater: [] });
  const [bolag, setBolag] = useState({ status: 'idle', data: null, fel: '' });
  const [sok, setSok] = useState({ status: 'idle', traffar: [], fel: '' });
  const [kontakter, setKontakter] = useState([]);
  const [kontakt, setKontakt] = useState(TOM_KONTAKT);
  const [leadVal, setLeadVal] = useState('ny');
  const [leadFalt, setLeadFalt] = useState({ titel: '', beskrivning: '' });
  const [mote, setMote] = useState(TOM_MOTE);
  const [skapar, setSkapar] = useState(false);
  const [fel, setFel] = useState('');
  const [resultat, setResultat] = useState(null);
  const [kallelse, setKallelse] = useState('');
  const [signatur, setSignatur] = useState({ html: '', text: '', senderName: '', senderEmail: '' });
  const hamtatOrgNr = useRef('');
  const soktNamn = useRef('');
  const amneAndrat = useRef(false);

  const avsandare = signatur.senderName || userProfile?.displayName || currentUser?.displayName || '';
  const flatbox = useMemo(() => products.find(isFlatboxProduct), [products]);
  const mvl = useMemo(() => departments.find(d => d.name?.toUpperCase().includes('MVL')), [departments]);
  const befintligKund = kund.val !== 'ny' ? companies.find(c => c.id === kund.val) : null;
  const oppna = befintligKund ? oppnaLeads(befintligKund.id, leads) : [];
  const kundnamn = befintligKund?.name || kund.namn;

  function aterstall() {
    setRaw(''); setTolkad(null); setKund(TOM_KUND); setMatch({ via: null, kandidater: [] });
    setBolag({ status: 'idle', data: null, fel: '' }); setSok({ status: 'idle', traffar: [], fel: '' });
    setKontakter([]); setKontakt(TOM_KONTAKT); setLeadVal('ny'); setLeadFalt({ titel: '', beskrivning: '' });
    setMote(TOM_MOTE); setFel(''); setResultat(null); setKallelse('');
    hamtatOrgNr.current = ''; soktNamn.current = ''; amneAndrat.current = false;
  }

  // Signaturen hämtas från samma källa som i "Boka möte" från lead
  useEffect(() => {
    if (!open || !currentUser) return;
    let aktuell = true;
    loadMeetingSignature(currentUser).then(s => { if (aktuell) setSignatur(s); });
    return () => { aktuell = false; };
  }, [open, currentUser]);

  // Ämnet följer vald kund tills det har redigerats för hand
  useEffect(() => {
    if (!tolkad || amneAndrat.current) return;
    setMote(m => ({ ...m, amne: flatboxSubject(kundnamn) }));
  }, [kundnamn, tolkad]);

  function stang() {
    if (skapar) return;
    if (resultat) onSkapad?.(resultat);
    aterstall();
    onClose();
  }

  // ── Tolkning ──
  function klistraIn(text) {
    setRaw(text);
    const t = tolkaBokningsmail(text);
    setTolkad(t);
    if (!t) return;
    const m = matchaKund(t.kund, companies);
    setMatch(m);
    setKund({
      val: m.kandidater[0]?.id || 'ny',
      namn: t.kund.namn,
      orgNr: t.kund.orgNr ? formateraOrgNr(t.kund.orgNr) : '',
      ort: t.kund.ort,
      telefon: t.kund.telefon,
      bransch: t.kund.bransch,
      antalAnstallda: t.kund.antalAnstallda,
      omsattning: t.kund.omsattning,
      webbplats: t.doman ? `www.${t.doman}` : '',
      comdevaKundnummer: t.kund.comdevaKundnummer,
    });
    setKontakt({ val: 'ny', ...t.kontakt });
    setLeadFalt({ titel: `${m.kandidater[0]?.name || t.kund.namn} – Flatbox`, beskrivning: t.internAnteckning });
    setMote({
      ...t.mote,
      amne: amneAndrat.current ? mote.amne : flatboxSubject(m.kandidater[0]?.name || t.kund.namn),
      kallelsetext: flatboxStandardText(avsandare),
    });
  }

  // ── Befintlig kund: kontakter, kontaktmatchning och öppna leads ──
  useEffect(() => {
    if (!befintligKund) { setKontakter([]); setKontakt(k => ({ ...k, val: 'ny' })); setLeadVal('ny'); return; }
    let aktuell = true;
    laddaKontakter(befintligKund.id).then(list => {
      if (!aktuell) return;
      setKontakter(list);
      const k = matchaKontakt(tolkad?.kontakt.epost, list);
      setKontakt(prev => ({ ...prev, val: k ? k.id : 'ny' }));
    });
    const o = oppnaLeads(befintligKund.id, leads);
    setLeadVal(o[0]?.id || 'ny');
    setLeadFalt(l => ({ ...l, titel: `${befintligKund.name} – Flatbox` }));
    return () => { aktuell = false; };
  }, [befintligKund?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Org.nr: giltigt nummer hämtar bolagsdata direkt ──
  useEffect(() => {
    if (kund.val !== 'ny') return;
    const nr = giltigtOrgNr(kund.orgNr);
    if (!nr || nr === hamtatOrgNr.current) return;
    hamtatOrgNr.current = nr;
    setBolag({ status: 'laddar', data: null, fel: '' });
    hamtaBolag(nr)
      .then(data => {
        setBolag({ status: 'klar', data, fel: '' });
        setKund(k => ({
          ...k,
          ort: k.ort || data.address?.city || '',
          bransch: k.bransch || data.sniCodes?.[0]?.label || '',
          webbplats: k.webbplats || data.website || '',
        }));
      })
      .catch(e => { hamtatOrgNr.current = ''; setBolag({ status: 'fel', data: null, fel: e.message }); });
  }, [kund.orgNr, kund.val]);

  // ── Namnsökning när ny kund saknar org.nr ──
  useEffect(() => {
    if (kund.val !== 'ny' || giltigtOrgNr(kund.orgNr) || !tolkad) return;
    const namn = tolkad.kund.namn;
    if (!namn || namn.length < 3 || soktNamn.current === namn) return;
    soktNamn.current = namn;
    setSok({ status: 'laddar', traffar: [], fel: '' });
    sokForetag(namn)
      .then(traffar => {
        setSok({ status: 'klar', traffar, fel: '' });
        const ort = (tolkad.kund.ort || '').toLowerCase();
        const sammaOrt = ort ? traffar.filter(t => (t.city || '').toLowerCase() === ort) : [];
        if (sammaOrt.length === 1) setKund(k => ({ ...k, orgNr: formateraOrgNr(sammaOrt[0].orgnr) }));
      })
      .catch(e => setSok({ status: 'fel', traffar: [], fel: e.message }));
  }, [kund.val, tolkad]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Skapa ──
  const kanSkapa = tolkad && (kund.val !== 'ny' || kund.namn.trim())
    && (kontakt.val !== 'ny' || kontakt.fornamn.trim() || kontakt.epost.trim())
    && mote.start && mote.slut;

  async function skapa() {
    setSkapar(true);
    setFel('');
    try {
      const b = bolag.data;
      const orgNr = giltigtOrgNr(kund.orgNr);
      const kundFalt = {
        name: kund.namn.trim(),
        orgNumber: orgNr ? formateraOrgNr(orgNr) : (orgNrSiffror(kund.orgNr) ? kund.orgNr : ''),
        comdevaKundnummer: kund.comdevaKundnummer,
        city: kund.ort, phone: kund.telefon, industry: kund.bransch,
        employees: kund.antalAnstallda, revenue: kund.omsattning, website: kund.webbplats,
        departmentIds: mvl ? [mvl.id] : [],
        ...(b ? {
          address: b.address?.street || '', zip: b.address?.zip || '',
          visitStreet: b.visitingAddress?.street || '', visitZip: b.visitingAddress?.zip || '',
          visitCity: b.visitingAddress?.city || '', description: b.description || '',
          companyForm: b.companyForm || '', fSkatt: b.fSkatt ?? null, stabilityGrade: b.stabilityGrade || '',
          registeredDate: b.registeredDate || '', sniCodes: b.sniCodes?.length ? JSON.stringify(b.sniCodes) : '',
        } : {}),
      };
      const kontaktNamn = [kontakt.fornamn, kontakt.efternamn].filter(Boolean).join(' ').trim();
      const valdKontakt = kontakt.val !== 'ny' ? kontakter.find(c => c.id === kontakt.val) : null;
      const befintligtLead = leadVal !== 'ny' ? leads.find(l => l.id === leadVal) : null;

      const ids = await skapaFranBokningsmail({
        kund: { befintligId: befintligKund?.id || null, falt: kundFalt },
        kontakt: {
          befintligId: valdKontakt?.id || null,
          falt: valdKontakt
            ? { name: valdKontakt.name, email: valdKontakt.email }
            : { name: kontaktNamn, role: kontakt.titel, mobile: kontakt.mobil, email: kontakt.epost.trim().toLowerCase() },
        },
        lead: {
          befintligt: befintligtLead,
          falt: {
            title: leadFalt.titel.trim() || `${kundnamn} – Flatbox`,
            description: leadFalt.beskrivning,
            type: 'produkt',
            productId: flatbox?.id || '',
            departmentIds: mvl ? [mvl.id] : [],
            ownerId: currentUser.uid,
            storlek: null,
            offerStatus: '', hasSandbox: false, tags: [],
          },
        },
        mote,
        raw,
        uid: currentUser.uid,
      });
      setResultat({
        ...ids,
        kopplatTillBefintligt: !!befintligtLead,
        epost: valdKontakt?.email || kontakt.epost.trim().toLowerCase(),
        kontaktNamn: valdKontakt?.name || kontaktNamn,
      });
    } catch (e) {
      setFel(`Inget skapades – ${e.message || e}`);
    } finally {
      setSkapar(false);
    }
  }

  // ── Kallelse ──
  const kallelseData = resultat && {
    amne: mote.amne, start: mote.start, slut: mote.slut, plats: mote.plats, text: mote.kallelsetext,
    epost: resultat.epost, kontaktnamn: resultat.kontaktNamn, kundnamn,
    signatureHtml: signatur.html, signatureText: signatur.text,
  };

  async function oppnaOutlook() {
    window.open(outlookDeeplink(kallelseData), '_blank', 'noopener');
    setKallelse('öppnad i Outlook');
    markeraKallelseOppnad(resultat.leadId, resultat.activityId, 'outlook').catch(() => {});
  }

  function laddaNerIcs() {
    const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const m = moteForIcs(kallelseData);
    const ics = buildIcs(m, {
      organizerName: avsandare, organizerEmail: signatur.senderEmail || currentUser.email || '',
      uid: `bokning-${resultat.activityId}@lagbasn`, stamp, includeAttendees: true, method: 'PUBLISH',
    });
    downloadIcs(ics, icsFilename(m));
    setKallelse('nedladdad som ICS');
    markeraKallelseOppnad(resultat.leadId, resultat.activityId, 'ics').catch(() => {});
  }

  // ── Rendering ──
  const kundFalt = (key, label, sx = f()) => (
    <TextField size="small" label={label} sx={sx} value={kund[key]}
      disabled={kund.val !== 'ny'} onChange={e => setKund(k => ({ ...k, [key]: e.target.value }))} />
  );
  const kontaktFalt = (key, label, sx = f()) => (
    <TextField size="small" label={label} sx={sx} value={kontakt[key]}
      disabled={kontakt.val !== 'ny'} onChange={e => setKontakt(k => ({ ...k, [key]: e.target.value }))} />
  );

  return (
    <Dialog open={open} onClose={stang} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EventIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">Nytt från bokningsmail</Typography>
            <Typography variant="caption" color="text.secondary">
              Klistra in mailet från Comdeva – kund, kontakt, lead och möte fylls i
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />

      {resultat ? (
        <DialogContent sx={{ pt: 3 }}>
          <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mb: 2 }}>
            {resultat.kopplatTillBefintligt ? 'Mötet är kopplat till det befintliga leadet.' : 'Kund, kontakt, lead och möte är skapade.'}
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Öppna kallelsen, granska den, slå på Teams vid behov och skicka själv.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={oppnaOutlook}
              sx={{ bgcolor: '#0F6CBD', '&:hover': { bgcolor: '#0c5a9e' }, fontWeight: 700 }}>
              Öppna kallelse i Outlook
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={laddaNerIcs}>
              Ladda ner ICS
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Deltagare: {resultat.epost || '–'}</Typography>
              <KopieraKnapp text={resultat.epost} />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Outlook på webben: ingen fetstil eller signatur – använd ICS-filen för formaterad kallelse.
          </Typography>
          {kallelse && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#2E7D32' }}>
              Kallelsen markerad som {kallelse}.
            </Typography>
          )}
        </DialogContent>
      ) : (
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField multiline minRows={tolkad ? 2 : 8} maxRows={tolkad ? 3 : 16} fullWidth autoFocus
            placeholder="Klistra in hela bokningsmailet här"
            value={raw} onChange={e => klistraIn(e.target.value)} />
          {raw && !tolkad && (
            <Alert severity="warning">Texten ser inte ut som ett bokningsmail från Comdeva (hittar varken Företag/Person, Kundnummer eller Beskrivning).</Alert>
          )}

          {tolkad && (
            <>
              {/* 1. Kund */}
              <Block nr={1} titel="Kund"
                hoger={<Chip size="small" label={befintligKund ? `Befintlig${match.via ? ` (via ${match.via === 'orgnr' ? 'org.nr' : match.via === 'doman' ? 'domän' : 'namn'})` : ''}` : 'Ny kund'}
                  sx={{ bgcolor: befintligKund ? '#E8F5E9' : '#FFF3E0', color: befintligKund ? '#2E7D32' : '#E65100', fontWeight: 700 }} />}>
                {match.kandidater.length > 0 && (
                  <TextField select size="small" label={match.kandidater.length > 1 ? 'Flera kunder matchar – välj' : 'Matchad kund'}
                    value={kund.val} onChange={e => setKund(k => ({ ...k, val: e.target.value }))}>
                    {match.kandidater.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}{c.orgNumber ? ` · ${c.orgNumber}` : ''}{c.city ? ` · ${c.city}` : ''}
                      </MenuItem>
                    ))}
                    <MenuItem value="ny">+ Skapa ny kund</MenuItem>
                  </TextField>
                )}
                {befintligKund ? (
                  <Typography variant="body2">
                    <Link component="button" type="button" onClick={() => window.open(`/crm/companies/${befintligKund.id}`, '_blank')}>
                      {befintligKund.name}
                    </Link>
                    {befintligKund.orgNumber ? ` · ${befintligKund.orgNumber}` : ''}{befintligKund.city ? ` · ${befintligKund.city}` : ''}
                  </Typography>
                ) : (
                  <>
                    <Box sx={rad}>
                      {kundFalt('namn', 'Namn *', f('2 1 240px'))}
                      <TextField size="small" label="Org.nr" sx={f('1 1 160px')} value={kund.orgNr}
                        onChange={e => setKund(k => ({ ...k, orgNr: e.target.value }))}
                        error={!!kund.orgNr && orgNrSiffror(kund.orgNr) !== '' && !giltigtOrgNr(kund.orgNr)}
                        helperText={kund.comdevaKundnummer && !giltigtOrgNr(kund.comdevaKundnummer)
                          ? `Comdeva-nr ${kund.comdevaKundnummer} är inget org.nr` : ' '}
                        InputProps={{ endAdornment: bolag.status === 'laddar' ? <CircularProgress size={14} /> : null }} />
                      <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} sx={{ alignSelf: 'flex-start', mt: 0.25 }}
                        onClick={() => window.open(allabolagSokUrl(kund.namn), '_blank', 'noopener')}>
                        Sök på allabolag
                      </Button>
                    </Box>
                    {bolag.status === 'klar' && (
                      <Alert severity="success" sx={{ py: 0 }}>
                        Bolagsverket: <strong>{bolag.data.name}</strong>
                        {bolag.data.address?.city ? ` · ${bolag.data.address.city}` : ''}
                        {bolag.data.companyForm ? ` · ${bolag.data.companyForm}` : ''}
                        {normaliseraForetagsnamn(bolag.data.name) !== normaliseraForetagsnamn(kund.namn) && (
                          <Button size="small" sx={{ ml: 1 }} onClick={() => setKund(k => ({ ...k, namn: bolag.data.name }))}>
                            Använd namnet
                          </Button>
                        )}
                      </Alert>
                    )}
                    {bolag.status === 'fel' && <Alert severity="error" sx={{ py: 0 }}>{bolag.fel}</Alert>}
                    {!giltigtOrgNr(kund.orgNr) && sok.status === 'laddar' && (
                      <Typography variant="caption" color="text.secondary"><CircularProgress size={10} /> Söker på namnet i BolagsAPI…</Typography>
                    )}
                    {!giltigtOrgNr(kund.orgNr) && sok.status === 'klar' && sok.traffar.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Träffar i BolagsAPI – klicka för att välja:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                          {sok.traffar.map(t => (
                            <Chip key={t.orgnr} size="small" variant="outlined"
                              label={`${t.name} · ${t.city || 'ort saknas'} · ${formateraOrgNr(t.orgnr)}`}
                              onClick={() => setKund(k => ({ ...k, orgNr: formateraOrgNr(t.orgnr) }))} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {!giltigtOrgNr(kund.orgNr) && sok.status === 'klar' && sok.traffar.length === 0 && (
                      <Typography variant="caption" color="text.secondary">Inga träffar på namnet i BolagsAPI.</Typography>
                    )}
                    {sok.status === 'fel' && (
                      <Typography variant="caption" color="text.secondary">
                        Namnsökning ej tillgänglig ({sok.fel}). Använd "Sök på allabolag" och klistra in org.nr.
                      </Typography>
                    )}
                    <Box sx={rad}>
                      {kundFalt('ort', 'Ort')}
                      {kundFalt('telefon', 'Telefon')}
                      {kundFalt('bransch', 'Bransch')}
                    </Box>
                    <Box sx={rad}>
                      {kundFalt('antalAnstallda', 'Antal anställda')}
                      {kundFalt('omsattning', 'Omsättning (enhet okänd)')}
                      {kundFalt('webbplats', 'Webbplats')}
                    </Box>
                    <Typography variant="caption" color="text.secondary">Org.nr är frivilligt och kan fyllas i senare.</Typography>
                  </>
                )}
              </Block>

              {/* 2. Kontakt */}
              <Block nr={2} titel="Kontakt (huvudkontakt på leadet)"
                hoger={<Chip size="small" label={kontakt.val !== 'ny' ? 'Befintlig' : 'Ny kontakt'}
                  sx={{ bgcolor: kontakt.val !== 'ny' ? '#E8F5E9' : '#FFF3E0', color: kontakt.val !== 'ny' ? '#2E7D32' : '#E65100', fontWeight: 700 }} />}>
                {kontakter.length > 0 && (
                  <TextField select size="small" label="Kontakt hos kunden" value={kontakt.val}
                    onChange={e => setKontakt(k => ({ ...k, val: e.target.value }))}>
                    <MenuItem value="ny">+ Ny kontakt</MenuItem>
                    {kontakter.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ''}</MenuItem>)}
                  </TextField>
                )}
                {kontakt.val === 'ny' && (
                  <>
                    <Box sx={rad}>
                      {kontaktFalt('fornamn', 'Förnamn')}
                      {kontaktFalt('efternamn', 'Efternamn')}
                      {kontaktFalt('titel', 'Titel')}
                    </Box>
                    <Box sx={rad}>
                      {kontaktFalt('mobil', 'Mobil')}
                      {kontaktFalt('epost', 'E-post', f('2 1 240px'))}
                    </Box>
                  </>
                )}
              </Block>

              {/* 3. Lead */}
              <Block nr={3} titel="Lead">
                {oppna.length > 0 && (
                  <Alert severity="warning">
                    Det finns redan ett öppet lead – koppla mötet dit i stället?
                    <RadioGroup value={leadVal} onChange={e => setLeadVal(e.target.value)} sx={{ mt: 0.5 }}>
                      {oppna.map(l => (
                        <FormControlLabel key={l.id} value={l.id} control={<Radio size="small" />}
                          label={(
                            <span>
                              Koppla till befintligt:{' '}
                              <Link href={`/crm/leads/${l.id}`} target="_blank" rel="noopener">{l.title}</Link>
                            </span>
                          )} />
                      ))}
                      <FormControlLabel value="ny" control={<Radio size="small" />} label="Skapa nytt lead" />
                    </RadioGroup>
                  </Alert>
                )}
                {leadVal === 'ny' && (
                  <>
                    <TextField size="small" label="Titel" value={leadFalt.titel}
                      onChange={e => setLeadFalt(l => ({ ...l, titel: e.target.value }))} />
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip size="small" label={flatbox ? `Produkt: ${flatbox.name}` : 'Produkt FLATBOX saknas'}
                        sx={{ bgcolor: flatbox ? '#FF6B00' : '#FFEBEE', color: flatbox ? 'white' : '#C62828' }} />
                      <Chip size="small" label={`Källa: ${LEAD_KALLA_COMDEVA}`} />
                      <Chip size="small" label="Skede: Demo" sx={{ bgcolor: '#E0F2F1', color: '#00897B' }} />
                      <Chip size="small" label="Demo Bokad" sx={{ bgcolor: '#E0F2F1', color: '#00897B', border: '1px solid #00897B55' }} />
                      {mvl && <Chip size="small" label="MVL" sx={{ bgcolor: '#1a1a1a', color: 'white' }} />}
                    </Box>
                    <TextField size="small" multiline minRows={2} maxRows={6} label="Intern anteckning (Möteskommentar)"
                      value={leadFalt.beskrivning} onChange={e => setLeadFalt(l => ({ ...l, beskrivning: e.target.value }))} />
                  </>
                )}
              </Block>

              {/* 4. Möte */}
              <Block nr={4} titel="Möte">
                <Box sx={rad}>
                  <TextField size="small" type="datetime-local" label="Start *" InputLabelProps={{ shrink: true }} sx={f()}
                    value={mote.start} onChange={e => setMote(m => ({ ...m, start: e.target.value }))} />
                  <TextField size="small" type="datetime-local" label="Slut *" InputLabelProps={{ shrink: true }} sx={f()}
                    value={mote.slut} onChange={e => setMote(m => ({ ...m, slut: e.target.value }))} />
                  <TextField size="small" label="Plats" sx={f()} value={mote.plats}
                    onChange={e => setMote(m => ({ ...m, plats: e.target.value }))} />
                </Box>
                <TextField size="small" label="Ämne" value={mote.amne}
                  onChange={e => { amneAndrat.current = true; setMote(m => ({ ...m, amne: e.target.value })); }} />
                <Tooltip title="Texten kunden ser i kallelsen. Möteskommentaren är intern och följer inte med." placement="top-start">
                  <TextField size="small" multiline minRows={4} label="Kallelsetext (till kunden)" value={mote.kallelsetext}
                    onChange={e => setMote(m => ({ ...m, kallelsetext: e.target.value }))} />
                </Tooltip>
              </Block>
              {fel && <Alert severity="error">{fel}</Alert>}
            </>
          )}
        </DialogContent>
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {resultat ? (
          <>
            <Button onClick={() => { const id = resultat.leadId; stang(); navigate(`/crm/leads/${id}`); }}>
              Öppna leadet
            </Button>
            <Button variant="contained" onClick={stang} sx={{ bgcolor: '#1a1a1a' }}>Klar</Button>
          </>
        ) : (
          <>
            <Button onClick={stang} disabled={skapar}>Avbryt</Button>
            <Button variant="contained" onClick={skapa} disabled={!kanSkapa || skapar}
              startIcon={skapar ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
              sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, fontWeight: 700 }}>
              {skapar ? 'Skapar…' : 'Skapa'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
